import { setDocumentTitle } from 'common/src/vtj/browser/page-title'
import ErrorPageType from 'common/src/vtj/ErrorPageType.enum'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Block, Heading, Link, Paragraph, Text } from 'suomifi-ui-components'
import { ErrorMainHeading } from 'todistukset-ui/src/vtj/components/error-page/ErrorPage.sc'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  ContentBlock,
  Main,
} from 'todistukset-ui/src/vtj/components/page-content/PageContent.sc'

interface ErrorTextKey {
  title: string
  description: string
}

const errorTextKeys: Record<string, ErrorTextKey> = {
  [ErrorPageType.PAGE_NOT_FOUND]: {
    title: 'errorPagePageNotFoundTitle',
    description: 'errorPagePageNotFoundDescription',
  },
  [ErrorPageType.GENERIC_ERROR]: {
    title: 'errorPageGenericErrorTitle',
    description: 'errorPageGenericErrorDescription',
  },
  [ErrorPageType.SESSION_NOT_FOUND]: {
    title: 'errorPageSessionNotFoundTitle',
    description: 'errorPageSessionNotFoundDescription',
  },
  [ErrorPageType.TODISTUS_NOT_FOUND]: {
    title: 'errorPageTodistusNotFoundTitle',
    description: 'errorPageTodistusNotFoundDescription',
  },
  [ErrorPageType.ORDER_ID_NOT_FOUND]: {
    title: 'errorPageOrderIdNotFoundTitle',
    description: 'errorPageOrderIdNotFoundDescription',
  },
  [ErrorPageType.PAYMENT_FAILED]: {
    title: 'errorPagePaymentFailedTitle',
    description: 'errorPagePaymentFailedDescription',
  },
  [ErrorPageType.ORDER_NOT_FOUND]: {
    title: 'errorPageOrderNotFoundTitle',
    description: 'errorPageOrderNotFoundDescription',
  },
  [ErrorPageType.RECEIPT_NOT_FOUND]: {
    title: 'errorPageReceiptNotFoundTitle',
    description: 'errorPageReceiptNotFoundDescription',
  },
  [ErrorPageType.ORDER_NOT_PAID]: {
    title: 'errorPageOrderNotPaidTitle',
    description: 'errorPageOrderNotPaidDescription',
  },
  [ErrorPageType.PAYMENT_PENDING_OR_DELAYED]: {
    title: 'errorPagePaymentPendingOrDelayedTitle',
    description: 'errorPagePaymentPendingOrDelayedDescription',
  },
}

export interface ErrorPageProps {
  lang: string
  errors: string[]
}

const ErrorPage = (props: ErrorPageProps): React.JSX.Element => {
  const { lang, errors } = props

  const { pathname, search } = useLocation()
  const currentUrl = [pathname, search].join('')
  const loginPagePath = `/auth/suomifi/login?RelayState=${encodeURIComponent(
    currentUrl
  )}`

  const homePagePath = `/${lang}/`

  const showLoginButton = errors.indexOf(ErrorPageType.SESSION_NOT_FOUND) >= 0

  const title = getMainTitle(errors)
  const { t } = useTranslation()
  setDocumentTitle(t(title))

  return (
    <Main id="main-content">
      <ErrorMainHeading variant="h1" data-test-id="errorPageTitle">
        <LocalizedString stringKey={title} />
      </ErrorMainHeading>
      <ContentBlock padding="xl">
        {renderContent(errors)}
        {showLoginButton && (
          <Block variant="section">
            <Link
              href={loginPagePath}
              data-test-id="error-page-login-button"
              underline="initial"
            >
              <LocalizedString stringKey="errorPageLoginButtonLabel" />
            </Link>
          </Block>
        )}
        <Block variant="section">
          <Link href={homePagePath} underline="initial">
            <LocalizedString stringKey="errorPageLandingPageLinkLabel" />
          </Link>
        </Block>
      </ContentBlock>
    </Main>
  )
}

function getMainTitle(errors: readonly string[]): string {
  return errors.length > 1
    ? 'errorPageSeveralErrorsTitle'
    : getErrorTitle(errors[0])
}

function getErrorTitle(error: string): string {
  return error in errorTextKeys
    ? errorTextKeys[error].title
    : errorTextKeys[ErrorPageType.GENERIC_ERROR].title
}

function getErrorDescription(error: string): string {
  return error in errorTextKeys
    ? errorTextKeys[error].description
    : errorTextKeys[ErrorPageType.GENERIC_ERROR].description
}

function renderContent(errors: readonly string[]): React.JSX.Element[] {
  return errors.length > 1
    ? errors.map((err, i) => (
        <Block variant="section" role="alert" key={i} data-test-id={err}>
          <Heading variant="h2">
            <Text variant="lead">
              <LocalizedString stringKey={getErrorTitle(err)} />
            </Text>
          </Heading>
          <Paragraph>
            <LocalizedString stringKey={getErrorDescription(err)} />
          </Paragraph>
        </Block>
      ))
    : errors.map((err) => (
        <Block variant="section" role="alert" key={err} data-test-id={err}>
          <Paragraph>
            <LocalizedString stringKey={getErrorDescription(err)} />
          </Paragraph>
        </Block>
      ))
}

export default ErrorPage
